import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ArrowsCrossingLine from '@/assets/icons/arrows_crossing_line.svg?component';
import ArrowsUpDownLine from '@/assets/icons/arrows_up_down_line.svg?component';
import BarsDescendingFill from '@/assets/icons/bars_descending_fill.svg?component';
import BasketHarvestFill from '@/assets/icons/basket_harvest_fill.svg?component';
import BoatFill from '@/assets/icons/boat_fill.svg?component';
import PathMergeLine from '@/assets/icons/path_merge_line.svg?component';
import PathSplitLine from '@/assets/icons/path_split_line.svg?component';
import { Overline } from '@/components/atoms/Eyebrow';
import type { IconComponent } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { parseLocale } from '@/utils/localeFormatters';

import { trackStyle } from '../../helpers/trackStyle';
import type { BadgeKind } from '../../types';
import Glyph from '../Glyph/Glyph';
import { TRACK_TIP_DELAY } from '../GroupTrack/TrackTip';

import styles from './EventBadge.module.scss';

export const BADGE_ICONS: Record<BadgeKind, IconComponent> = {
    delousing: BoatFill,
    move: ArrowsUpDownLine,
    split: PathSplitLine,
    grading: BarsDescendingFill,
    redistribution: ArrowsCrossingLine,
    merge: PathMergeLine,
    harvest: BasketHarvestFill,
};

interface EventIconProps {
    kind: BadgeKind;
    /** The legend's smaller disc, without the ring that lifts a badge off the bars. */
    compact?: boolean;
}

export function EventIcon(props: EventIconProps) {
    const { kind, compact = false } = props;
    return (
        <span
            className={classNames(styles.icon, styles[`icon--${kind}`], {
                [styles.iconCompact]: compact,
            })}
            aria-hidden="true">
            <Glyph icon={BADGE_ICONS[kind]} />
        </span>
    );
}

interface EventBadgeProps {
    kinds: BadgeKind[];
    leftPct: number;
    showLabel?: boolean;
    /** The other groups in a redistribution, named in the tooltip and accessible name. */
    peers?: string[];
}

/**
 * One or more event icons on a track, with the combined label above ("Delousing + Move"). A
 * redistribution's tooltip and accessible name also list the other groups in it.
 */
export default function EventBadge(props: EventBadgeProps) {
    const { kinds, leftPct, showLabel = true, peers = [] } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const list = useMemo(
        () => new Intl.ListFormat(parseLocale(i18n.language), { type: 'conjunction' }),
        [i18n.language]
    );
    const label = kinds.map((kind) => t(`board.events.${kind}`)).join(t('board.events.separator'));
    const name =
        peers.length > 0
            ? t('board.events.withGroups', { events: label, groups: list.format(peers) })
            : label;

    return (
        <div className={styles.badge} style={trackStyle(leftPct)}>
            {showLabel && <Overline className={styles.label}>{label}</Overline>}
            <Tooltip content={name} layout="fit" delay={TRACK_TIP_DELAY}>
                <span className={styles.icons} role="img" aria-label={name}>
                    {kinds.map((kind) => (
                        <EventIcon key={kind} kind={kind} />
                    ))}
                </span>
            </Tooltip>
        </div>
    );
}
